import {
	AMM_RESERVE_PRECISION,
	BN,
	SPOT_MARKET_IMF_PRECISION,
	SPOT_MARKET_WEIGHT_PRECISION,
	SpotMarketAccount,
	ZERO,
	TEN,
	calculateSizeDiscountAssetWeight,
} from '@velocity-exchange/sdk';

export type IAWDataPoint = {
	x: number;
	y: number;
};

/**
 * Calculate pure individual size discount asset weight
 * This calculates ONLY the size discount based on IMF factor,
 * without considering global deposit scaling
 */
export function calculatePureIndividualAssetWeight(
	positionSize: BN,
	spotMarketAccount: SpotMarketAccount
): BN {
	// If position size is zero, return the market's default initial asset weight (no size discount)
	if (positionSize.eq(ZERO)) {
		return new BN(spotMarketAccount.initialAssetWeight);
	}

	// Convert position to AMM_RESERVE_PRECISION
	const sizePrecision = TEN.pow(new BN(spotMarketAccount.decimals));
	let sizeInAmmReservePrecision: BN;
	if (sizePrecision.gt(AMM_RESERVE_PRECISION)) {
		sizeInAmmReservePrecision = positionSize.div(
			sizePrecision.div(AMM_RESERVE_PRECISION)
		);
	} else {
		sizeInAmmReservePrecision = positionSize
			.mul(AMM_RESERVE_PRECISION)
			.div(sizePrecision);
	}

	// Calculate size discount starting from the market's initial asset weight
	// This applies ONLY the individual size discount, not global scaling
	return calculateSizeDiscountAssetWeight(
		sizeInAmmReservePrecision,
		new BN(spotMarketAccount.imfFactor),
		new BN(spotMarketAccount.initialAssetWeight)
	);
}

// Generate chart data for individual size discount
export function generateIndividualChartData(
	spotMarketAccount: SpotMarketAccount | undefined
): IAWDataPoint[] {
	const data: IAWDataPoint[] = [];

	if (!spotMarketAccount) {
		return data;
	}

	const imfFactor = new BN(spotMarketAccount.imfFactor);
	const initialAssetWeight = new BN(spotMarketAccount.initialAssetWeight);

	const initialIAWPercent =
		initialAssetWeight.mul(new BN(100)).toNumber() /
		SPOT_MARKET_WEIGHT_PRECISION.toNumber();

	// Target is 50% of initial IAW
	const targetIAWPercent = initialIAWPercent / 4;

	// If IMF factor is 0, no discount occurs, draw horizontal line
	if (imfFactor.eq(ZERO)) {
		data.push({
			x: 0,
			y: initialIAWPercent,
		});
		// Add a second point far on the x-axis to create a horizontal line
		// Use a reasonable large value (e.g., 1 million tokens)
		data.push({
			x: 1000000000,
			y: initialIAWPercent,
		});
		return data;
	}

	// Reverse calculation: for each Y (IAW %), calculate required X (position size)
	// Generate Y values from initial down to target in 0.1% decrements
	const yValues: number[] = [];
	for (
		let y = Math.floor(initialIAWPercent * 10);
		y >= Math.ceil(targetIAWPercent * 10);
		y--
	) {
		yValues.push(y / 10);
	}

	// Start with x=0, y=initial IAW (no position = full weight)
	data.push({
		x: 0,
		y: initialIAWPercent,
	});

	// Helper to calculate position size for a target IAW using inverse IMF formula
	const calculatePositionForIAW = (targetIAWPercent: number): number => {
		// Convert target IAW percent to SPOT_MARKET_WEIGHT_PRECISION units
		const targetIAW = new BN(
			Math.round(
				(targetIAWPercent * SPOT_MARKET_WEIGHT_PRECISION.toNumber()) / 100
			)
		);

		// IMF formula: IAW = imfNumerator / (imfDenominator + sizeSqrt * imfFactor / 100000)
		// where imfNumerator = 1.1 * SPOT_MARKET_IMF_PRECISION
		// and sizeSqrt = sqrt(size * 10 + 1) in AMM_RESERVE_PRECISION

		const imfNumerator = SPOT_MARKET_IMF_PRECISION.add(
			SPOT_MARKET_IMF_PRECISION.div(new BN(10))
		); // 1.1M

		// Solve for sizeSqrt:
		// targetIAW = (imfNumerator * SPOT_MARKET_WEIGHT_PRECISION) / (SPOT_MARKET_IMF_PRECISION + sizeSqrt * imfFactor / 100000)
		// targetIAW * (SPOT_MARKET_IMF_PRECISION + sizeSqrt * imfFactor / 100000) = imfNumerator * SPOT_MARKET_WEIGHT_PRECISION
		// SPOT_MARKET_IMF_PRECISION + sizeSqrt * imfFactor / 100000 = (imfNumerator * SPOT_MARKET_WEIGHT_PRECISION) / targetIAW
		// sizeSqrt * imfFactor / 100000 = (imfNumerator * SPOT_MARKET_WEIGHT_PRECISION) / targetIAW - SPOT_MARKET_IMF_PRECISION
		// sizeSqrt = [(imfNumerator * SPOT_MARKET_WEIGHT_PRECISION) / targetIAW - SPOT_MARKET_IMF_PRECISION] * 100000 / imfFactor

		const numerator = imfNumerator.mul(SPOT_MARKET_WEIGHT_PRECISION);
		const denominator = targetIAW;
		const quotient = numerator.div(denominator);
		const difference = quotient.sub(SPOT_MARKET_IMF_PRECISION);
		const sizeSqrt = difference.mul(new BN(100_000)).div(imfFactor);

		// sizeSqrt = sqrt(size * 10 + 1)
		// sizeSqrt^2 = size * 10 + 1
		// size = (sizeSqrt^2 - 1) / 10
		const sizeSqrtSquared = sizeSqrt.mul(sizeSqrt);
		const sizeInAmmReservePrecision = sizeSqrtSquared
			.sub(new BN(1))
			.div(new BN(10));

		// Convert from AMM_RESERVE_PRECISION to token decimals
		const tokenPrecision = new BN(10).pow(new BN(spotMarketAccount.decimals));

		let positionSize: BN;
		if (tokenPrecision.gt(AMM_RESERVE_PRECISION)) {
			positionSize = sizeInAmmReservePrecision.mul(
				tokenPrecision.div(AMM_RESERVE_PRECISION)
			);
		} else {
			positionSize = sizeInAmmReservePrecision.div(
				AMM_RESERVE_PRECISION.div(tokenPrecision)
			);
		}

		// Convert BN to tokens safely (avoid Number overflow for large values)
		const wholePart = positionSize.div(tokenPrecision);
		const fractionalPart = positionSize.mod(tokenPrecision);

		// Check if number is too large for safe conversion
		const MAX_SAFE_BN = new BN(Number.MAX_SAFE_INTEGER);
		if (wholePart.gt(MAX_SAFE_BN)) {
			// Use string conversion for very large numbers (loses some precision but avoids overflow)
			const wholeStr = wholePart.toString();
			const fracStr = fractionalPart
				.toString()
				.padStart(spotMarketAccount.decimals, '0');
			const combinedStr = wholeStr + '.' + fracStr;
			const positionInTokens = parseFloat(combinedStr);
			return positionInTokens;
		}

		const positionInTokens =
			wholePart.toNumber() +
			fractionalPart.toNumber() / tokenPrecision.toNumber();
		return positionInTokens;
	};

	// Generate data points for each Y value
	yValues.forEach((y) => {
		// Skip if target IAW would round to zero (prevents division by zero)
		const targetIAWValue = Math.round(
			(y * SPOT_MARKET_WEIGHT_PRECISION.toNumber()) / 100
		);
		if (targetIAWValue <= 0) {
			return;
		}

		const x = calculatePositionForIAW(y);

		data.push({
			x: x,
			y: y,
		});
	});

	return data;
}
